import type {
	Address,
	AddressData,
	Addresses
} from '~~/core/entities/Addresses';

import { normalizeString } from '@/utils/index';

// Lunr types for the dynamically imported module
type LunrIndex = {
	search: (query: string) => Array<{ ref: string; score: number }>;
};

type LunrBuilder = {
	ref: (field: string) => void;
	field: (field: string, options?: { boost: number }) => void;
	pipeline: { remove: (fn: unknown) => void };
	add: (doc: Record<string, unknown>) => void;
};

type LunrModule = {
	(config: (this: LunrBuilder) => void): LunrIndex;
	stemmer: unknown;
};

/**
 * Interface para resultados de busca mapeados
 */
export interface MappedSearchResult extends AddressData {
	score: number;
}

/**
 * Tipos de índice suportados
 */
type IndexType = 'original' | 'multi-city';

/**
 * Cache de índices Lunr por tipo
 */
interface IndexCacheEntry {
	regionsIndex: LunrIndex | null;
	citiesIndex: LunrIndex | null;
	regionsData: AddressData[];
	citiesData: Address[];
	dataHash: string;
}

// Cached lunr module
let lunrModule: LunrModule | null = null;

/**
 * Load lunr module dynamically
 */
async function getLunr(): Promise<LunrModule> {
	if (lunrModule) return lunrModule;

	// Dynamic import - only loads when search is first used
	const module = await import('lunr');
	lunrModule = module.default as LunrModule;
	return lunrModule;
}

/**
 * Gera um hash para detectar mudanças nos dados de endereços.
 * Considera todos os elementos do array para evitar resultados stale.
 */
function generateDataHash(addresses: Addresses[]): string {
	if (addresses.length === 0) return '';
	// Gera um hash mais robusto considerando todos os endereços
	const summary = addresses
		.map(
			addr =>
				`${addr.state || ''}:${addr.regions?.length || 0}:${addr.cities?.length || 0}`
		)
		.join(',');
	return `${addresses.length}-${summary}`;
}

/**
 * Cria um índice Lunr para regiões
 */
async function buildRegionsIndex(regions: AddressData[]): Promise<LunrIndex> {
	const lunr = await getLunr();

	const documents = regions.map((region, idx) => ({
		id: idx,
		label: normalizeString(region.label),
		city: normalizeString(region.data.city),
		macroregion: normalizeString(region.data.macroregion)
	}));

	return lunr(function (this: LunrBuilder) {
		this.ref('id');
		this.field('label', { boost: 10 });
		this.field('city', { boost: 5 });
		this.field('macroregion', { boost: 4 });
		this.pipeline.remove(lunr.stemmer);
		documents.forEach(doc => this.add(doc));
	});
}

/**
 * Cria um índice Lunr para cidades
 */
async function buildCitiesIndex(cities: Address[]): Promise<LunrIndex> {
	const lunr = await getLunr();

	const documents = cities.map((city, idx) => ({
		id: idx,
		label: normalizeString(city.label)
	}));

	return lunr(function (this: LunrBuilder) {
		this.ref('id');
		this.field('label');
		this.pipeline.remove(lunr.stemmer);
		documents.forEach(doc => this.add(doc));
	});
}

/**
 * Serviço singleton para gerenciar índices de busca de endereços
 * Evita recriação de índices Lunr em cada instância do composable
 */
class AddressSearchService {
	private indexCache: Map<IndexType, IndexCacheEntry> = new Map();
	private pendingUpdates: Map<IndexType, Promise<void>> = new Map();

	/**
	 * Atualiza os índices para um tipo específico
	 * Só recria se os dados mudaram
	 */
	async updateIndices(
		type: IndexType,
		regions: AddressData[],
		cities: Address[],
		addresses: Addresses[]
	): Promise<void> {
		const newHash = generateDataHash(addresses);
		const cached = this.indexCache.get(type);

		// Se o hash é igual, não precisa recriar
		if (cached && cached.dataHash === newHash) {
			return;
		}

		// Avoid duplicate updates
		const pending = this.pendingUpdates.get(type);
		if (pending) {
			return pending;
		}

		const updatePromise = (async () => {
			// Cria novos índices apenas se houver dados
			const [regionsIndex, citiesIndex] = await Promise.all([
				regions.length > 0 ? buildRegionsIndex(regions) : null,
				cities.length > 0 ? buildCitiesIndex(cities) : null
			]);

			this.indexCache.set(type, {
				regionsIndex,
				citiesIndex,
				regionsData: regions,
				citiesData: cities,
				dataHash: newHash
			});

			this.pendingUpdates.delete(type);
		})();

		this.pendingUpdates.set(type, updatePromise);
		return updatePromise;
	}

	/**
	 * Busca regiões usando o índice Lunr
	 */
	searchRegions(
		type: IndexType,
		searchTerm: string | undefined | null
	): MappedSearchResult[] {
		const cached = this.indexCache.get(type);
		if (!cached) return [];

		const { regionsIndex, regionsData } = cached;

		// Se não há termo de busca, retorna todas ordenadas
		if (!searchTerm || searchTerm.trim() === '') {
			return regionsData
				.map(region => ({ ...region, score: 1 }))
				.sort((a, b) => a.label.localeCompare(b.label));
		}

		// Se não há índice, retorna vazio
		if (!regionsIndex) return [];

		const normalizedTerm = normalizeString(searchTerm);

		return regionsIndex
			.search(`*${normalizedTerm}*`)
			.map(result => {
				const region = regionsData[parseInt(result.ref)];
				return region ? { ...region, score: result.score } : null;
			})
			.filter((item): item is MappedSearchResult => item !== null)
			.sort((a, b) => {
				if (b.score !== a.score) return b.score - a.score;
				return a.label.localeCompare(b.label);
			});
	}

	/**
	 * Busca cidades usando o índice Lunr
	 */
	searchCities(
		type: IndexType,
		searchTerm: string | undefined | null
	): Address[] {
		const cached = this.indexCache.get(type);
		if (!cached) return [];

		const { citiesIndex, citiesData } = cached;

		// Se não há termo de busca, retorna todas ordenadas
		if (!searchTerm || searchTerm.trim() === '') {
			return [...citiesData].sort((a, b) => a.label.localeCompare(b.label));
		}

		// Se não há índice, retorna vazio
		if (!citiesIndex) return [];

		const normalizedTerm = normalizeString(searchTerm);

		return citiesIndex
			.search(`*${normalizedTerm}*`)
			.map(result => citiesData[parseInt(result.ref)])
			.filter((city): city is Address => city !== undefined)
			.sort((a, b) => a.label.localeCompare(b.label));
	}

	/**
	 * Verifica se os índices para um tipo estão inicializados
	 */
	hasIndices(type: IndexType): boolean {
		const cached = this.indexCache.get(type);
		return cached !== undefined && cached.dataHash !== '';
	}

	/**
	 * Limpa o cache de índices (útil para testes ou reinicialização)
	 */
	clearCache(type?: IndexType): void {
		if (type) {
			this.indexCache.delete(type);
		} else {
			this.indexCache.clear();
		}
	}

	/**
	 * Retorna os dados de regiões cacheados
	 */
	getRegionsData(type: IndexType): AddressData[] {
		return this.indexCache.get(type)?.regionsData || [];
	}

	/**
	 * Retorna os dados de cidades cacheados
	 */
	getCitiesData(type: IndexType): Address[] {
		return this.indexCache.get(type)?.citiesData || [];
	}
}

// Singleton instance - criado apenas uma vez no lado do cliente
let serviceInstance: AddressSearchService | null = null;

/**
 * Retorna a instância singleton do serviço de busca de endereços
 * No servidor, retorna uma nova instância para cada request (evita vazamento entre requests)
 */
export function getAddressSearchService(): AddressSearchService {
	// No servidor, sempre cria nova instância para evitar compartilhamento entre requests
	if (import.meta.server) {
		return new AddressSearchService();
	}

	// No cliente, usa singleton
	if (!serviceInstance) {
		serviceInstance = new AddressSearchService();
	}
	return serviceInstance;
}

/**
 * Hook para usar o serviço de busca de endereços de forma reativa
 */
export function useAddressSearchService() {
	const service = getAddressSearchService();

	return {
		updateIndices: service.updateIndices.bind(service),
		searchRegions: service.searchRegions.bind(service),
		searchCities: service.searchCities.bind(service),
		hasIndices: service.hasIndices.bind(service),
		clearCache: service.clearCache.bind(service),
		getRegionsData: service.getRegionsData.bind(service),
		getCitiesData: service.getCitiesData.bind(service)
	};
}
